import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider, Layout, theme } from 'antd';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import Blog from './pages/Blog/Blog';
import BlogPost from './pages/BlogPost/BlogPost';
import TermsOfService from './pages/TermsOfService/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import Callback from './pages/Callback/Callback';
import Logout from './pages/Logout/Logout';
import SignIn from './pages/SignIn/SignIn';
import NotFound from './pages/NotFound/NotFound';
import AppStore from './pages/AppStore/AppStore';
import Invite from './pages/Invite/Invite';
import InviteAccept from './pages/Invite/InviteAccept';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import PersonaRoute from './components/PersonaRoute/PersonaRoute';
import DataSyncOnAuth from './components/DataSyncOnAuth/DataSyncOnAuth';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Clients from './pages/Clients/Clients';
import AddClient from './pages/Clients/AddClient/AddClient';
import ClientCRM from './pages/ClientCRM/ClientCRM';
import ItineraryBuilder from './pages/ItineraryBuilder/ItineraryBuilder';
import ListBuilder from './pages/ListBuilder/ListBuilder';
import BackendTest from './pages/BackendTest/BackendTest';
import Trips from './pages/Trips/Trips';
import AddTrip from './pages/Trips/AddTrip/AddTrip';
import TripDetail from './pages/TripDetail/TripDetail';
import TripEdit from './pages/TripEdit/TripEdit';
import DayView from './pages/DayView/DayView';
import TripShareInbox from './pages/TripShareInbox/TripShareInbox';
import Settings from './pages/Settings/Settings';
import TravelerPreferences from './pages/Settings/TravelerPreferences';
import ChecklistTemplate from './pages/Settings/ChecklistTemplate';
import AllowedSenders from './pages/Settings/AllowedSenders';
import UserProfile from './pages/Settings/UserProfile';
import styles from './App.module.css';

const { Content } = Layout;

function AppContent(): React.JSX.Element {
  const { effectiveTheme } = useTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: effectiveTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: 'var(--primary-color)',
          fontFamily: 'var(--font-family-primary)',
          borderRadius: 14,
        },
      }}
    >
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Layout className={styles.App}>
          <Header />
          <Content className={styles.mainContent}>
              <ErrorBoundary>
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/callback" element={<Callback />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signin/pro" element={<SignIn />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/app" element={<AppStore />} />
                <Route path="/invite/:inviteId/accept" element={
                  <ProtectedRoute>
                    <InviteAccept />
                  </ProtectedRoute>
                } />
                <Route path="/invite/:inviteId" element={<Invite />} />
                <Route path="/qr/:token" element={<Invite />} />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/traveler-preferences"
                  element={
                    <ProtectedRoute>
                      <TravelerPreferences />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/checklist-template"
                  element={
                    <ProtectedRoute>
                      <ChecklistTemplate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/allowed-senders"
                  element={
                    <ProtectedRoute>
                      <AllowedSenders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/profile"
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/trips"
                  element={
                    <PersonaRoute require="user">
                      <Trips />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/trips/shared"
                  element={
                    <PersonaRoute require="user">
                      <TripShareInbox />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/trips/new"
                  element={
                    <PersonaRoute require="user">
                      <AddTrip />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/trips/new/edit"
                  element={
                    <PersonaRoute require="user">
                      <TripEdit />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/trips/:tripId/edit"
                  element={
                    <PersonaRoute require="user">
                      <TripEdit />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/trips/:tripId/days/:date"
                  element={
                    <PersonaRoute require="user">
                      <DayView />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/trips/:tripId/plan"
                  element={
                    <PersonaRoute require="user">
                      <ItineraryBuilder />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/trips/:tripId"
                  element={
                    <PersonaRoute require="user">
                      <TripDetail />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/clients"
                  element={
                    <PersonaRoute require="pro">
                      <Clients />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/clients/new"
                  element={
                    <PersonaRoute require="pro">
                      <AddClient />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/clients/:clientId/plan/:tripId"
                  element={
                    <PersonaRoute require="pro">
                      <ItineraryBuilder />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/clients/:clientId"
                  element={
                    <PersonaRoute require="pro">
                      <ClientCRM />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/list-builder"
                  element={
                    <PersonaRoute require="pro">
                      <ListBuilder />
                    </PersonaRoute>
                  }
                />
                <Route
                  path="/backend-test"
                  element={
                    <ProtectedRoute>
                      <BackendTest />
                    </ProtectedRoute>
                  }
                />
                {/* Catch-all route for unknown paths - must be last */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </ErrorBoundary>
          </Content>
          <Footer />
        </Layout>
      </Router>
    </ConfigProvider>
  );
}

function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <DataSyncOnAuth />
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
